import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Smartphone,
  Leaf,
  AlertTriangle,
  CreditCard,
  Clock,
  MapPin,
  RecycleIcon,
  Calendar,
  MessageCircle,
  Mail,
  Phone,
  QrCode,
  ChevronDown,
  ChevronUp,
  BarChart2,
  Award,
} from 'lucide-react';
import GlassNavbar from '../components/GlassNavbar';
import Footer from '../components/Footer';
import ComingSoon from '../components/ComingSoon';
import { LINKS } from '../config/links';

const SERVICE_CARDS = [
  {
    id: 'incidencias',
    icon: AlertTriangle,
    title: 'Reportar Incidencia',
    desc: 'Informa de problemas de limpieza, contenedores, playas, jardines y otros servicios municipales.',
    color: '#C2410C',
    bgColor: 'rgba(194,65,12,0.08)',
    actions: [
      { label: 'Reportar ahora', url: '/reportar-incidencia', internal: true },
    ],
  },
  {
    id: 'ecoparque',
    icon: Leaf,
    title: 'Mi Ecoparque',
    desc: 'Accede a tu historial de entregas, consulta tus puntos PAYT, descarga albaránes y gestiona tu perfil.',
    color: '#1F8A70',
    bgColor: 'rgba(31,138,112,0.08)',
    actions: [{ label: 'Acceder al portal', url: LINKS.ACTAIS_CITIZEN_WEB }],
  },
  {
    id: 'nfc',
    icon: CreditCard,
    title: 'Tarjeta NFC orgánico',
    desc: 'Solicita y activa tu tarjeta NFC para abrir los contenedores de fracción orgánica de tu zona.',
    color: '#1E5AA8',
    bgColor: 'rgba(207,227,245,0.5)',
    actions: [{ label: 'Solicitar tarjeta', url: LINKS.SOLICITUD_NFC }],
  },
];

const INFO_SECTIONS = [
  {
    icon: Clock,
    title: 'Horarios del Ecoparque',
    content:
      'Lunes a viernes: 08:00–15:00 y 16:00–19:00. Sábados: 09:00–14:00. Domingos y festivos: cerrado. Consulta las variaciones estacionales en la app o en el panel municipal.',
  },
  {
    icon: MapPin,
    title: 'Ubicaciones y contenedores',
    content:
      'Encontrarás contenedores de RSU, recicló (amarillo, azul, verde) y orgánico (marrón) distribuidos por todo el municipio. Descarga la app para ver el mapa interactivo o usa el buscador por dirección.',
  },
  {
    icon: RecycleIcon,
    title: 'Qué se puede depositar',
    content:
      'Ecoparque: muebles, electrónicos, aceites, pilas, escombros, poda y residuos peligrosos domésticos. Contenedores de calle: envases (amarillo), papel/cartón (azul), vidrio (verde), orgánico (marrón), resto (gris).',
  },
  {
    icon: Calendar,
    title: 'Calendario de recogida',
    content:
      'La recogida de residuos se realiza diariamente en zonas de alta densidad y tres veces por semana en zonas residenciales. Las playas tienen servicio especial en temporada alta (junio–septiembre).',
  },
];

const INDICATORS = [
  { id: 1, label: 'Frecuencia de recogida', value: '100%', desc: 'Servicio diario garantizado' },
  { id: 2, label: 'Limpieza viaria', value: '97%', desc: 'Cumplimiento diario' },
  { id: 3, label: 'Disponibilidad Ecoparque', value: '100%', desc: 'Apertura sin interrupciones' },
  { id: 4, label: 'Tiempo respuesta incidencias', value: '98%', desc: 'En plazo definido' },
  { id: 5, label: 'Satisfacción ciudadana', value: '8.4/10', desc: 'Encuesta semestral' },
  { id: 6, label: 'Tasa de reciclaje', value: '73%', desc: 'Fracción seca' },
];

function ServiceCard({ id, icon: Icon, title, desc, color, bgColor, actions }) {
  return (
    <div className="rounded-3xl border border-[#CFE3F5] bg-white p-8 hover:shadow-md transition-shadow">
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
        style={{ background: bgColor }}
      >
        <Icon size={26} style={{ color }} aria-hidden="true" />
      </div>
      <h3 className="font-display text-2xl text-[#0A2540] mb-3">{title}</h3>
      <p className="text-sm text-[#6F8AA8] leading-relaxed mb-6">{desc}</p>
      <div className="flex flex-wrap gap-3">
        {actions.map(({ label, url, internal }) => {
          // Si es ruta interna, usar Link de React Router
          if (internal) {
            return (
              <Link
                key={label}
                to={url}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium hover:opacity-80 transition-opacity"
                style={{
                  background: bgColor,
                  color,
                  border: `1px solid ${color}22`,
                }}
              >
                {label} →
              </Link>
            );
          }
          // Si no, usar ComingSoon
          return (
            <ComingSoon
              key={label}
              url={url}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium"
              style={{
                background: bgColor,
                color,
                border: `1px solid ${color}22`,
              }}
            >
              {label} →
            </ComingSoon>
          );
        })}
      </div>
    </div>
  );
}

export default function Ciudadania() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Ciudadanía | Cuidemos Los Alcázares®</title>
        <meta
          name="description"
          content="Servicios municipales de recogida, limpieza y reciclaje en Los Alcázares. App, Ecoparque, incidencias y tarjeta NFC."
        />
        <meta property="og:title" content="Ciudadanía | Cuidemos Los Alcázares®" />
        <meta
          property="og:description"
          content="Accede a todos los servicios municipales de recogida, limpieza y reciclaje"
        />
      </Helmet>

      {/* Hero section for ciudadania */}
      <section
        className="relative pt-20 pb-16 px-6 overflow-hidden"
        aria-labelledby="ciudadania-heading"
        style={{ minHeight: '520px' }}
      >
        {/* Background image real — Mar Menor */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1586715725848-5076bbcba8ef?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-center"
          />
          {/* Overlay azul marino sobre la foto */}
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(135deg, rgba(10,37,64,0.88) 0%, rgba(30,90,168,0.70) 60%, rgba(10,37,64,0.60) 100%)' }}
          />
        </div>

        <div className="max-w-7xl mx-auto text-center relative z-10" style={{ paddingTop: '5rem' }}>
          <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase bg-white/15 text-white border border-white/25 mb-6 backdrop-blur-sm">
            Ciudadanía · Los Alcázares
          </span>
          <h1 id="ciudadania-heading" className="font-display text-5xl sm:text-6xl text-white mb-5">
            Tu municipio,
            <br />
            <em style={{ color: '#7DB9E8', fontStyle: 'italic' }}>más limpio</em>.
          </h1>
          <p className="text-white/80 text-base sm:text-lg max-w-xl mx-auto leading-relaxed mb-10">
            Accede a todos los servicios municipales de recogida, limpieza y reciclaje
            de Los Alcázares desde un único portal.
          </p>
          <a
            href="#servicios"
            className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-white text-[#0A2540] text-base font-semibold hover:bg-[#CFE3F5] transition-colors"
          >
            Explorar servicios
          </a>
        </div>
      </section>

      {/* Service cards */}
      <section
        id="servicios"
        className="py-20 px-6 bg-[#FAFCFE]"
        aria-labelledby="servicios-heading"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 id="servicios-heading" className="font-display text-4xl sm:text-5xl text-[#0A2540]">
              Servicios para ti
            </h2>
            <p className="mt-4 text-[#6F8AA8] text-base max-w-xl mx-auto leading-relaxed">
              Todo lo que necesitas a un clic: la app, el Ecoparque, reportar incidencias o solicitar tu tarjeta NFC.
            </p>
          </div>
          <div
            data-testid="ciudadania-services"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {SERVICE_CARDS.map((card) => (
              <ServiceCard key={card.id} {...card} />
            ))}
          </div>
        </div>
      </section>

      {/* Información del servicio */}
      <section className="py-20 px-6 bg-white" aria-labelledby="info-heading">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 id="info-heading" className="font-display text-4xl sm:text-5xl text-[#0A2540]">
              Información útil
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {INFO_SECTIONS.map(({ icon: Icon, title, content }) => (
              <div
                key={title}
                className="rounded-2xl border border-[#CFE3F5] bg-[#FAFCFE] p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[#CFE3F5] flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-[#1E5AA8]" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-sans font-semibold text-[#0A2540] mb-2">{title}</h3>
                    <p className="text-sm text-[#6F8AA8] leading-relaxed">{content}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QR section */}
      <section
        id="qr"
        className="py-20 px-6 bg-[#0A2540]"
        aria-labelledby="qr-heading"
      >
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-6">
            <QrCode size={30} className="text-white" aria-hidden="true" />
          </div>
          <h2 id="qr-heading" className="font-display text-4xl sm:text-5xl text-white mb-5">
            Has escaneado un QR
          </h2>
          <p className="text-[#7DB9E8] text-base sm:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
            Los códigos QR de los contenedores y mupis del municipio te dirigen aquí.
            Desde esta página puedes reportar una incidencia o acceder a los servicios municipales.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/reportar-incidencia"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-[#0A2540] text-sm font-medium hover:bg-[#CFE3F5] transition-colors"
            >
              Reportar incidencia →
            </Link>
            <ComingSoon
              url={LINKS.APP_IOS}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-white/30 text-white text-sm font-medium hover:bg-white/10 transition-colors"
            >
              Descargar app
            </ComingSoon>
          </div>
        </div>
      </section>

      {/* Compromiso ambiental */}
      <section className="py-20 px-6 bg-[#FAFCFE]" aria-labelledby="compromiso-heading">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium tracking-widest uppercase bg-[#CFE3F5] text-[#1E5AA8] mb-4">
              Transparencia
            </span>
            <h2 id="compromiso-heading" className="font-display text-4xl sm:text-5xl text-[#0A2540] mt-3">
              Nuestro compromiso contigo
            </h2>
            <p className="mt-4 text-[#6F8AA8] text-base max-w-xl mx-auto leading-relaxed">
              Los 10 indicadores de calidad del contrato, en versión simplificada para toda la ciudadanía.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {INDICATORS.map(({ id, label, value, desc }) => (
              <div
                key={id}
                className="rounded-2xl border border-[#CFE3F5] bg-white p-5 flex items-start gap-4"
              >
                <div className="w-10 h-10 rounded-xl bg-[#CFE3F5] flex items-center justify-center text-sm font-semibold text-[#1E5AA8] flex-shrink-0">
                  {id}
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0A2540] mb-0.5">{label}</p>
                  <p className="text-2xl font-display text-[#1E5AA8] tabular-nums">{value}</p>
                  <p className="text-xs text-[#6F8AA8] mt-0.5">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contacto */}
      <section id="contacto" className="py-20 px-6 bg-white" aria-labelledby="contacto-heading">
        <div className="max-w-4xl mx-auto text-center">
          <h2 id="contacto-heading" className="font-display text-4xl sm:text-5xl text-[#0A2540] mb-4">
            Contáctanos
          </h2>
          <p className="text-[#6F8AA8] mb-12">
            Estamos aquí para ayudarte. Elige el canal que prefieras.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              {
                icon: MessageCircle,
                label: 'WhatsApp',
                value: LINKS.WHATSAPP ? LINKS.WHATSAPP : 'Próximamente',
                href: LINKS.WHATSAPP ? `https://wa.me/${LINKS.WHATSAPP}` : null,
                color: '#25D366',
                bg: 'rgba(37,211,102,0.08)',
              },
              {
                icon: Mail,
                label: 'Email',
                value: LINKS.EMAIL_SERVICIO || 'losalcazareslimpio@valorizasm.com',
                href: `mailto:${LINKS.EMAIL_SERVICIO || 'losalcazareslimpio@valorizasm.com'}`,
                color: '#1E5AA8',
                bg: 'rgba(30,90,168,0.08)',
              },
              {
                icon: Phone,
                label: 'Teléfono',
                value: LINKS.TELEFONO || 'Próximamente',
                href: LINKS.TELEFONO ? `tel:${LINKS.TELEFONO}` : null,
                color: '#0A2540',
                bg: 'rgba(10,37,64,0.06)',
              },
            ].map(({ icon: Icon, label, value, href, color, bg }) => (
              <div
                key={label}
                className="rounded-2xl border border-[#CFE3F5] p-6 hover:shadow-md transition-shadow"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: bg }}
                >
                  <Icon size={22} style={{ color }} aria-hidden="true" />
                </div>
                <p className="text-xs font-semibold uppercase tracking-widest text-[#6F8AA8] mb-2">{label}</p>
                {href ? (
                  <a
                    href={href}
                    className="text-sm font-medium text-[#0A2540] hover:text-[#1E5AA8] transition-colors break-all"
                  >
                    {value}
                  </a>
                ) : (
                  <p className="text-sm text-[#6F8AA8]">{value}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
